import { t } from '../i18n';
/** Мелкие элементы управления: сегменты, переключатель, строка настройки. */
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import * as telegram from '../lib/telegram';
import { useScrollEdges } from '../lib/useScrollEdges';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedProps<T extends string> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedProps<T>): ReactElement {
  // Длинный список листается вбок; затемнение края подсказывает, что справа есть ещё.
  const { ref, measure, fadeClass } = useScrollEdges<HTMLDivElement>([options.length]);
  return (
    <div ref={ref} onScroll={measure} className={'segmented ' + fadeClass} role="group" aria-label={ariaLabel ?? ''}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => {
            telegram.hapticFeedback.selection();
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Маленькая метка над чипсом, например «скоро». */
  badge?: string;
  /** Цветная точка перед подписью, '#RRGGBB'. */
  dot?: string;
  /** Подпись набирается этим шрифтом: так выбирают шрифт субтитров. */
  fontFamily?: string;
  /** Круглый образец цвета '#RRGGBB' вместо подписи; подпись остаётся для экранного диктора. */
  swatch?: string;
}

function ArrowIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export interface ChipsProps<T extends string> {
  options: ReadonlyArray<ChipOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  /** Дополнительный класс ряда, например для отдельного вида чипсов. */
  className?: string;
  /** Стрелка «дальше» лежит поверх ряда с затуханием, а не рядом с ним. */
  overlay?: boolean;
}

/** Чёрный или белый — выбираем больший контраст с фоном #RRGGBB. */
function colorChipForeground(hex: string): string {
  const channels = [1, 3, 5].map((start) => {
    const value = Number.parseInt(hex.slice(start, start + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = channels[0]! * 0.2126 + channels[1]! * 0.7152 + channels[2]! * 0.0722;
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? '#000000' : '#FFFFFF';
}

/**
 * Ряд круглых чипсов с горизонтальной прокруткой: стиль субтитров, формат, цвет,
 * шрифт. Когда ряд не помещается, по краям появляются стрелки.
 */
export function Chips<T extends string>({ options, value, onChange, ariaLabel, className, overlay }: ChipsProps<T>): ReactElement {
  const { ref: rowRef, edges: arrows, measure, fadeClass } = useScrollEdges<HTMLDivElement>([options.length]);

  const scroll = (direction: -1 | 1): void => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollBy({ left: direction * row.clientWidth * 0.6, behavior: 'smooth' });
  };

  return (
    <div className={'chips' + (overlay ? ' overlay' : '') + (className ? ' ' + className : '')}>
      {arrows.left ? (
        <button type="button" className="chips-arrow left" aria-label={t('editor.chips.back')} onClick={() => scroll(-1)}>
          <ArrowIcon />
        </button>
      ) : null}
      <div ref={rowRef} className={'chips-row ' + fadeClass} role="group" aria-label={ariaLabel ?? ''} onScroll={measure}>
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              className={`chip-btn${option.dot ? ' color' : ''}${option.swatch ? ' swatch' : ''}${active ? ' active' : ''}`}
              aria-pressed={active}
              aria-label={option.swatch ? option.label : undefined}
              disabled={option.disabled}
              style={{
                ...(option.fontFamily ? { fontFamily: `'${option.fontFamily}', sans-serif` } : {}),
                ...(option.dot ? { '--chip-color': option.dot, '--chip-on-color': colorChipForeground(option.dot) } : {}),
              } as CSSProperties}
              onClick={() => {
                if (active) return;
                telegram.hapticFeedback.selection();
                onChange(option.value);
              }}
            >
              {option.badge ? <span className="chip-badge">{option.badge}</span> : null}
              {option.dot ? <span className="chip-dot" aria-hidden="true" /> : null}
              {option.swatch ? <span className="chip-swatch" style={{ background: option.swatch }} aria-hidden="true" /> : option.label}
            </button>
          );
        })}
      </div>
      {arrows.right ? (
        <button type="button" className="chips-arrow right" aria-label={t('editor.chips.next')} onClick={() => scroll(1)}>
          <ArrowIcon />
        </button>
      ) : null}
    </div>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}

export function Switch({ checked, onChange, label }: SwitchProps): ReactElement {
  return (
    <button
      type="button"
      className="switch"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => {
        telegram.hapticFeedback.selection();
        onChange(!checked);
      }}
    />
  );
}

export interface SettingRowProps {
  title: string;
  hint?: string;
  children: ReactNode;
  /** Управление под заголовком, а не справа. */
  stacked?: boolean;
}

export function SettingRow({ title, hint, children, stacked }: SettingRowProps): ReactElement {
  if (stacked) {
    return (
      <div className="card">
        <div className="col">
          <h3>{title}</h3>
          {hint ? <span className="small muted">{hint}</span> : null}
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className="card">
      <div className="row between">
        <div className="col grow">
          <h3>{title}</h3>
          {hint ? <span className="small muted">{hint}</span> : null}
        </div>
        {children}
      </div>
    </div>
  );
}
